import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'
import { initializeUserKeys, verifyUserPassword } from '../utils/keyManagement'
import { storePassword, clearPassword } from '../utils/passwordHandling'
import logger from '../utils/logger'
import { translateAuthError } from '../utils/authErrors'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  initialized: boolean
  signUp: (email: string, password: string, captchaToken?: string | null) => Promise<void>
  signIn: (email: string, password: string, captchaToken?: string | null) => Promise<void>
  signOut: () => Promise<void>
  initialize: () => Promise<void>
  setUser: (user: User | null, session: Session | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => {
      // Wewnętrzna funkcja czyszcząca wszystkie powiązane store'y przy wylogowaniu.
      // Wywoływana zarówno przez signOut() jak i przez handler SIGNED_OUT w onAuthStateChange.
      const clearAllStores = async () => {
        // Wyczyść cache preferencji użytkownika
        try {
          const { useUserPreferencesStore } = await import('./userPreferencesStore')
          useUserPreferencesStore.getState().clearCache()
        } catch (prefError) {
          logger.error('❌ Błąd czyszczenia cache preferencji:', prefError)
        }

        // Wyczyść cache kalendarza (włącznie z selectedHome)
        try {
          const { useCalendarStore } = await import('./calendarStore')
          useCalendarStore.getState().clearCache()
        } catch (calendarError) {
          logger.error('❌ Błąd czyszczenia cache kalendarza:', calendarError)
        }

        // Wyczyść familyStore - KLUCZOWE!
        try {
          logger.debug('🏠 Czyszczenie familyStore...')
          const { useFamilyStore } = await import('./familyStore')
          const familyStore = useFamilyStore.getState()
          // Resetuj stan familyStore
          familyStore.clearCache()
          // Ważne: resetuj również initialized flag!
          useFamilyStore.setState({
            initialized: false,
            family: null,
            members: [],
            children: [],
            isLoading: false,
            error: null
          })
          // Wyczyść też persistowane dane familyStore z localStorage
          localStorage.removeItem('2domy-family-storage')
          logger.debug('✅ FamilyStore wyczyszczony')
        } catch (familyError) {
          logger.error('❌ Błąd czyszczenia familyStore:', familyError)
        }
      }

      return {
        user: null,
        session: null,
        loading: true,
        initialized: false,

      signUp: async (email: string, password: string, captchaToken?: string | null) => {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // Since email confirmation is disabled in Supabase settings
            emailRedirectTo: undefined,
            captchaToken: captchaToken ?? undefined,
          }
        })
        
        if (error) {
          throw new Error(translateAuthError(error))
        }

        // User should be automatically signed in due to disabled email confirmation
        if (data.user && data.session) {
          // Inicjalizacja kluczy szyfrowania dla nowego użytkownika
          try {
            logger.debug('🔐 Inicjalizacja kluczy szyfrowania dla nowego użytkownika...')
            await initializeUserKeys(data.user.id, password)
            
            // Zapisz hasło w sesji na 24 godziny
            await storePassword(data.user.id, password, true, 24 * 60)
            
            logger.debug('✅ Klucze szyfrowania zainicjalizowane pomyślnie')
          } catch (keyError) {
            console.error('❌ Błąd inicjalizacji kluczy szyfrowania:', keyError)
            // Kontynuuj mimo błędu, użytkownik będzie mógł zainicjalizować klucze później
          }
          
          set({ user: data.user, session: data.session })

          // Po ustawieniu użytkownika, natychmiast załaduj preferencje (bez czekania na App)
          try {
            const { useUserPreferencesStore } = await import('./userPreferencesStore')
            await useUserPreferencesStore.getState().loadPreferences()
          } catch (loadPrefError) {
            logger.error('❌ Błąd natychmiastowego ładowania preferencji po zalogowaniu:', loadPrefError)
          }

          // Zapewnij istnienie rodziny i członkostwa (parent_a) dla nowego użytkownika
          try {
            logger.debug('🏠 Sprawdzanie członkostwa w rodzinie...')
            // limit(1) zamiast maybeSingle(): przy kilku członkostwach maybeSingle() zwraca błąd
            // i null, a wtedy kod poniżej zakładałby kolejną rodzinę
            const { data: existingMembers, error: memberCheckError } = await supabase
              .from('family_members')
              .select('family_id')
              .eq('user_id', data.user.id)
              .limit(1)

            if (memberCheckError) {
              logger.error('❌ Błąd sprawdzania członkostwa:', memberCheckError)
            }

            // Przy błędzie zapytania nie wiemy, czy rodzina istnieje — nie zakładamy nowej
            if (!memberCheckError && (existingMembers ?? []).length === 0) {
              logger.debug('🏗️  Brak rodziny – tworzę domyślną rodzinę i przypisuję parent_a')
              const { data: familyData, error: familyError } = await supabase
                .from('families')
                .insert({ name: 'Rodzina', home_a_name: 'Dom A', home_b_name: 'Dom B' })
                .select()
                .single()

              if (familyError) {
                logger.error('❌ Błąd tworzenia domyślnej rodziny:', familyError)
              } else if (familyData) {
                const { error: addMemberError } = await supabase
                  .from('family_members')
                  .insert({
                    family_id: familyData.id,
                    user_id: data.user.id,
                    role: 'parent_a',
                    encryption_key: '',
                    invited_at: new Date().toISOString(),
                    joined_at: new Date().toISOString()
                  })

                if (addMemberError) {
                  logger.error('❌ Błąd dodawania członka rodziny:', addMemberError)
                } else {
                  // Wyczyść ewentualny cache z null
                  const cacheKey = `user-family-${data.user.id}`
                  sessionStorage.removeItem(cacheKey)
                  logger.debug('✅ Utworzono rodzinę i przypisano parent_a')
                }
              }
            } else {
              logger.debug('✅ Użytkownik ma już rodzinę')
            }
          } catch (familyInitError) {
            logger.error('❌ Błąd auto-inicjalizacji rodziny:', familyInitError)
          }
        }
      },

      signIn: async (email: string, password: string, captchaToken?: string | null) => {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: {
            captchaToken: captchaToken ?? undefined,
          }
        })
        
        if (error) {
          throw new Error(translateAuthError(error))
        }

        if (data.user && data.session) {
          // Weryfikacja kluczy szyfrowania
          try {
            logger.debug('🔐 Weryfikacja kluczy szyfrowania...')
            const isKeyValid = await verifyUserPassword(data.user.id, password)
            
            if (!isKeyValid) {
              logger.debug('⚠️ Klucze szyfrowania nie istnieją lub są niepoprawne, inicjalizacja...')
              await initializeUserKeys(data.user.id, password)
              logger.debug('✅ Klucze szyfrowania zainicjalizowane pomyślnie')
            } else {
              logger.debug('✅ Klucze szyfrowania zweryfikowane pomyślnie')
            }
            
            // Zapisz hasło w sesji na 24 godziny
            await storePassword(data.user.id, password, true, 24 * 60)
          } catch (keyError) {
            logger.error('❌ Błąd weryfikacji kluczy szyfrowania:', keyError)
            // Kontynuuj mimo błędu, użytkownik będzie mógł zainicjalizować klucze później
          }
          
          // Upewnij się, że użytkownik ma preferencje
          try {
            logger.debug('🔧 Sprawdzanie preferencji użytkownika...')
            const { data: userPrefs, error: prefsError } = await supabase
              .from('user_preferences')
              .select('id')
              .eq('user_id', data.user.id)
              .maybeSingle()
              
            // Jeśli nie ma preferencji, utwórz je
            if (!userPrefs && (prefsError?.code === 'PGRST116' || !prefsError)) {
              logger.debug('⚠️ Brak preferencji użytkownika, tworzenie...')
              const { error: insertError } = await supabase
                .from('user_preferences')
                .insert({
                  user_id: data.user.id,
                  color_scheme: 'default',
                  notifications_email: true,
                  notifications_calendar: true,
                  notifications_members: true,
                  updated_at: new Date().toISOString()
                })
                
              if (insertError) {
                logger.error('❌ Błąd tworzenia preferencji użytkownika:', insertError)
              } else {
                logger.debug('✅ Preferencje użytkownika utworzone')
              }
            } else if (prefsError) {
              logger.error('❌ Błąd sprawdzania preferencji użytkownika:', prefsError)
            } else {
              logger.debug('✅ Użytkownik ma już preferencje')
            }
          } catch (prefsError) {
            logger.error('❌ Błąd inicjalizacji preferencji użytkownika:', prefsError)
          }
          
          set({ user: data.user, session: data.session })
        }
      },

      signOut: async () => {
        logger.debug('🚪 Starting sign out process...')
        try {
          // Pobierz ID użytkownika przed wylogowaniem
          const { user } = _get()
          const userId = user?.id

          // 1. NAJPIERW wyloguj z Supabase — SDK potrzebuje tokenów do revokowania sesji
          logger.debug('🔒 Wylogowywanie z Supabase...')
          const { error } = await supabase.auth.signOut({ scope: 'global' })
          if (error) {
            console.error('❌ Supabase signOut error:', error)
          }
          logger.debug('✅ Supabase signOut successful')

          // 2. Wyczyść hasło E2EE
          if (userId) {
            logger.debug('🔑 Czyszczenie hasła z sesji i IndexedDB...')
            await clearPassword(userId)
          }

          // 3. Wyczyść sessionStorage
          logger.debug('🗑️ Czyszczenie wszystkich danych w sessionStorage...')
          sessionStorage.clear()

          // 4. Wyczyść dane w localStorage związane z autoryzacją
          logger.debug('🗑️ Czyszczenie danych autoryzacyjnych z localStorage...')
          const keysToRemove = []
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && (
                key.startsWith('sb-') ||
                key.includes('supabase') ||
                key === 'auth-storage' ||
                key === 'sb-2domy-auth-token')
            ) {
              keysToRemove.push(key)
            }
          }
          keysToRemove.forEach(key => {
            logger.debug(`🗑️ Usuwanie klucza z localStorage: ${key}`)
            localStorage.removeItem(key)
          })

          // 5. Wyczyść wszystkie powiązane store'y
          await clearAllStores()

          // NIE resetuj initialized - to powoduje pętlę!
          // Zachowaj initialized: true, aby zapobiec ponownej inicjalizacji
          set({ user: null, session: null, loading: false, initialized: true })
          logger.debug('✅ Auth state cleared - App router will handle redirect')
          
          // NIE wymuszaj przeładowania strony - React Router obsłuży przekierowanie
        } catch (error) {
          logger.error('💥 SignOut failed:', error)
          // Nawet w przypadku błędu, wyczyść stan autoryzacji i cache wszystkich store'ów
          await clearAllStores()

          // NIE resetuj initialized nawet przy błędzie - to powoduje pętlę!
          set({ user: null, session: null, loading: false, initialized: true })
          
          // NIE wymuszaj przeładowania strony - React Router obsłuży przekierowanie
          
          throw error
        }
      },

      setUser: (user: User | null, session: Session | null) => {
        set({ user, session })
      },

      initialize: async () => {
        logger.debug('🔄 Auth initialization starting...')
        set({ loading: true })
        
        try {
          // Zrezygnuj z agresywnego czyszczenia tokenów – pozwól Supabase odświeżyć sesję
          try {
            const storageKeys = ['sb-2domy-auth-token', 'auth-storage']
            storageKeys.forEach((k) => {
              const v = localStorage.getItem(k)
              if (!v) return
              try { JSON.parse(v) } catch {
                logger.debug(`🧹 Usuwanie uszkodzonego wpisu storage: ${k}`)
                localStorage.removeItem(k)
              }
            })
          } catch {}

          // Get current session with timeout
          logger.debug('📡 Getting session from Supabase...')
          const sessionPromise = supabase.auth.getSession()
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session timeout')), 30000)
          )
          
          const { data: { session }, error } = await Promise.race([
            sessionPromise,
            timeoutPromise
          ]) as any
          
          if (error) {
            logger.error('❌ Error getting session:', error)
          } else {
            logger.debug('✅ Session retrieved:', !!session, session?.user?.email)
          }

          logger.debug('🏁 Setting auth state...')
          set({ 
            user: session?.user ?? null, 
            session: session ?? null,
            loading: false,
            initialized: true 
          })
          logger.debug('✅ Auth initialization complete!')

          // Listen to auth changes
          logger.debug('🎧 Setting up auth state listener...')
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
              logger.debug('🔄 Auth state changed:', event, session?.user?.email)
              
              set({ 
                user: session?.user ?? null, 
                session: session ?? null,
                loading: false 
              })

              // Handle specific auth events
              if (event === 'PASSWORD_RECOVERY') {
                try {
                  // W trybie resetu hasła Supabase ustanawia tymczasową sesję
                  // Przekieruj użytkownika na stronę ustawienia nowego hasła
                  window.location.replace('/auth/update-password')
                } catch (navErr) {
                  logger.error('❌ Błąd przekierowania do update-password:', navErr)
                }
              } else if (event === 'SIGNED_IN') {
                logger.debug('✅ User signed in')
              } else if (event === 'SIGNED_OUT') {
                logger.debug('👋 User signed out — czyszczenie store\'ów...')
                await clearAllStores()
              }
            }
          )
          logger.debug('✅ Auth listener setup complete')

          // Cleanup subscription on unmount/page reload
          window.addEventListener('beforeunload', () => {
            subscription.unsubscribe()
          })

          // Odśwież sesję przy powrocie do aplikacji (24h UX)
          const refreshOnResume = async () => {
            try {
              const { data, error } = await supabase.auth.refreshSession()
              if (!error && data.session) {
                set({ user: data.session.user, session: data.session })
              }
            } catch {}
          }
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') refreshOnResume()
          })
          window.addEventListener('focus', refreshOnResume)

        } catch (error) {
          logger.error('💥 Auth initialization FAILED:', error)
          logger.error('💥 Error details:', error)
          set({ loading: false, initialized: true })

          // Cichy retry w tle – gdy Supabase odda sesję, ustaw stan bez blokowania UI
          try {
            supabase.auth.getSession().then(({ data: { session } }) => {
              set({ user: session?.user ?? null, session: session ?? null })
            }).catch(() => {})
          } catch {}
        }
      },
      }
    },
    {
      name: 'auth-storage',
      // Don't persist auth state - always re-initialize for security
      partialize: (_state) => ({}),
    }
  )
) 